import Vue from 'vue';
import Vuex from 'vuex';
import createMutationsSharer from 'vuex-shared-mutations';

import authentication from '@/services/api/authentication';
import tokenService from '../services/tokenService';
import cookiesService from '@/services/cookiesService';
import router from '@/router/index';

import familyData from '@/store/modules/familyData';
import reporterData from '@/store/modules/reporterData';
import provinces from '@/store/modules/provinces';
import defaultApi from '@/store/modules/defaultApi';
import familiesToAccept from '@/store/modules/familiesToAccept';
import mainMenu from '@/store/modules/mainMenu';
import acceptedFamilies from '@/store/modules/acceptedFamilies';
import volunteers from '@/store/modules/volunteers';
import family from '@/store/modules/family';
import sheet from '@/store/modules/sheet';
import token from '@/store/modules/token';
import auth from '@/store/modules/auth';
import search from '@/store/modules/search';
import pagination from '@/store/modules/pagination';
import crz from '@/store/modules/crz';
import customContent from '@/store/modules/customContent';
import description from '@/store/modules/description';
import warehouses from '@/store/modules/warehouses';
import warehouse from '@/store/modules/warehouse';
import previewForDonor from '@/store/modules/previewForDonor';
import giftgiver from '@/store/modules/giftgiver';
import giftGivingReport from '@/store/modules/giftGivingReport';
import audit from '@/store/modules/audit';
import swrUpload from '@/store/modules/swrUpload';
import specialistPackage from '@/store/modules/specialistPackage';
import furtherStory from '@/store/modules/furtherStory';

import { setUser } from '@sentry/vue';

Vue.use(Vuex);

const defaultErrorsObject = () => {
    return { status: null, statusText: null, visible: false };
};

const store = new Vuex.Store({
    plugins: [
        createMutationsSharer({
            predicate: [
                'token/activeUserAction',
                'token/setForcedToLogout',
                'token/syncModalInTabs',
                'token/setIsRefreshRequestSent'
            ]
        })
    ],
    modules: {
        familyData,
        family,
        reporterData,
        provinces,
        defaultApi,
        familiesToAccept,
        mainMenu,
        acceptedFamilies,
        volunteers,
        auth,
        sheet,
        token,
        search,
        pagination,
        crz,
        customContent,
        description,
        warehouses,
        warehouse,
        previewForDonor,
        giftgiver,
        giftGivingReport,
        audit,
        swrUpload,
        specialistPackage,
        furtherStory
    },
    state: {
        isLoggedIn: tokenService.isTokenValid(),
        errors: defaultErrorsObject(),
        isLoading: false
    },
    getters: {
        getErrors: (state) => state.errors,
        getIsLoggedIn: (state) => state.isLoggedIn,
        getIsLoading: (state) => state.isLoading,
        featuresVisible: () => {
            if (process.env.NODE_ENV !== 'production' && cookiesService.get('inProgress') === 'hide') {
                return false;
            }
            return process.env.VUE_APP_HIDE_FEATURES_IN_PROGRESS === 'false';
        }
    },
    mutations: {
        setLoggedIn (state, loginStatus) {
            state.isLoggedIn = loginStatus;
        },
        setErrors (state, errors) {
            state.errors = errors;
        },
        clearErrors (state) {
            state.errors = defaultErrorsObject();
        },
        setIsLoading (state, data) {
            state.isLoading = data;
        }
    },
    actions: {
        login (context, data) {
            context.commit('setIsLoading', true);
            return authentication
                .postAuth({
                    email: data.email,
                    password: data.password
                })
                .then(({ data }) => {
                    context.dispatch('auth/setAuthData', data);
                    context.commit('setLoggedIn', true);
                    context.dispatch('token/initTokenRegeneration');

                    const routeToRedirect = router.currentRoute.query?.previous ?? '/';

                    router.push({ path: routeToRedirect });
                    context.commit('setIsLoading', false);
                });
        },
        checkLogin (context) {
            if (tokenService.isTokenValid() === true) {
                context.commit('setLoggedIn', true);
                context.dispatch('token/initTokenRegeneration');
                setUser({
                    id: context.rootGetters['auth/getUser'] && context.rootGetters['auth/getUser'].id
                });
            } else {
                context.commit('setLoggedIn', false);
            }
        },
        logout (context) {
            if (tokenService.isTokenValid() === true) {
                authentication.logout().then(() => {
                    tokenService.setTokenExpirationTimestamp(0);
                });
            }
            context.commit('defaultApi/setAvailableWioStructs', []);
            this.dispatch('familiesToAccept/clearData');
            context.commit('setLoggedIn', false);
            window.localStorage.clear();
            context.commit('token/stop');
        }
    }
});

export default store;
export const useStore = () => store;
